import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { WebAuthnDevice } from "src/store/features/api/generated";
import { formatDistanceToNow, format } from "date-fns";

interface DeviceManagementTableProps {
  devices: WebAuthnDevice[];
  editingId: string | null;
  editName: string;
  onEditClick: (device: WebAuthnDevice) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditNameChange: (value: string) => void;
  onRemoveClick: (id: string) => void;
  isUpdatingDevice: boolean;
  isDeletingDevice: boolean;
}

export const DeviceManagementTable: React.FC<DeviceManagementTableProps> = ({
  devices,
  editingId,
  editName,
  onEditClick,
  onSaveEdit,
  onCancelEdit,
  onEditNameChange,
  onRemoveClick,
  isUpdatingDevice,
  isDeletingDevice,
}) => {
  return (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, boxShadow: "none", border: "1px solid #E5E9EB" }}>
      <Table sx={{ minWidth: 650 }} size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#1A1A1A" }}>
              Device Name
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#1A1A1A" }}>
              First Seen
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#1A1A1A" }}>
              Last Seen
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: "14px", color: "#1A1A1A" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => (
            <TableRow
              key={device.id}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: "5%",
                  width: "90%",
                  height: "1px",
                  backgroundColor: "#E5E9EB",
                },
              }}>
              <TableCell component="th" scope="row" sx={{ py: 2 }}>
                {editingId === device.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editName}
                    onChange={(e) => onEditNameChange(e.target.value)}
                    autoFocus
                    sx={{ maxWidth: 300 }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {device.displayName || device.browser}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(device.first_seen))} ago
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                    ({format(new Date(device.first_seen), "MMM d, yyyy")})
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(device.last_seen))} ago
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                    ({format(new Date(device.last_seen), "MMM d, yyyy")})
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ py: 2 }}>
                {editingId === device.id ? (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={onSaveEdit}
                      disabled={isUpdatingDevice}
                      variant="contained"
                      sx={{ minWidth: 80 }}>
                      {isUpdatingDevice ? <CircularProgress size={20} /> : "Save"}
                    </Button>
                    <Button size="small" onClick={onCancelEdit} color="inherit">
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => onEditClick(device)}
                      sx={{ color: "primary.main" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveClick(device.id)}
                      disabled={isDeletingDevice}
                      sx={{ color: "error.main" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
